// What the co-pilot panel knows and what it says (module 13 L2 to L18; the screen's artboards A to
// I). Pure: a reducer over the first read, the pushes and the person's clicks, and the words each
// state is drawn with, so every state the panel can be in is tested without a browser.
import { ApiError } from '../api.js';
import { dayMonth } from '../day.js';
import type { BotState, CardOutcome, CopilotCall, CopilotCallView, CopilotCard, CopilotEvent } from './api.js';

export interface PanelState {
  phase: 'loading' | 'ready' | 'failed';
  /** The last read's refusal, in words; the panel keeps what it had and says so. */
  error: string | null;
  enabled: boolean;
  call: CopilotCall | null;
  cards: CopilotCard[];
  /** When the latest bot push arrived, for the notice's time; unknown after a re-read. */
  botAt: string | null;
  /** Someone on the call is sharing a screen: the latest share push, or the call read's `sharing` on a (re)read. */
  sharing: boolean;
  /** The person asked to see the panel during this share (D: "Show anyway"). A new share hides it again. */
  showAnyway: boolean;
  /** Ducking hides the panel and only the panel (L15, L23); local to this window. */
  silenced: boolean;
  /** Bumped when a push names a call this page does not hold: the page reads the call again. */
  reread: number;
}

export type PanelAction =
  | { type: 'loaded'; view: CopilotCallView }
  | { type: 'failed'; message: string }
  | { type: 'event'; event: CopilotEvent; at: string }
  | { type: 'outcome'; cardId: string; outcome: CardOutcome }
  | { type: 'silence' } | { type: 'show' } | { type: 'show-anyway' };

export const panelStart: PanelState = {
  phase: 'loading', error: null, enabled: false, call: null, cards: [], botAt: null,
  sharing: false, showAnyway: false, silenced: false, reread: 0,
};

/**
 * A re-read carries no `heard` (the call's words are not kept, L8), so a card already on the
 * screen keeps the words it arrived with. Within the same call, a card pushed after the read was
 * answered is kept too: the read and the push race, and the push is the newer of the two.
 */
function mergeCards(held: CopilotCard[], read: CopilotCard[], sameCall: boolean): CopilotCard[] {
  const heldById = new Map(held.map((c) => [c.id, c]));
  const merged = read.map((c) => {
    const had = heldById.get(c.id);
    return had && had.heard && !c.heard ? { ...c, heard: had.heard } : c;
  });
  if (!sameCall) return merged;
  const readIds = new Set(read.map((c) => c.id));
  return [...merged, ...held.filter((c) => !readIds.has(c.id))];
}

export function panelReducer(s: PanelState, a: PanelAction): PanelState {
  switch (a.type) {
    case 'loaded': {
      const sameCall = !!s.call && !!a.view.call && s.call.botId === a.view.call.botId;
      const sharing = a.view.call?.sharing ?? s.sharing;
      return {
        ...s, phase: 'ready', error: null, enabled: a.view.enabled, call: a.view.call,
        cards: mergeCards(s.cards, a.view.cards, sameCall), botAt: sameCall ? s.botAt : null,
        sharing, showAnyway: sharing && s.sharing ? s.showAnyway : false,
      };
    }
    case 'failed':
      return { ...s, phase: s.phase === 'ready' ? 'ready' : 'failed', error: a.message };
    case 'outcome':
      return { ...s, cards: s.cards.map((c) => (c.id === a.cardId ? { ...c, outcome: a.outcome } : c)) };
    case 'silence':
      return { ...s, silenced: true };
    case 'show':
      return { ...s, silenced: false };
    case 'show-anyway':
      return { ...s, showAnyway: true };
    case 'event': {
      const e = a.event;
      if (e.kind === 'card') {
        const i = s.cards.findIndex((c) => c.id === e.card.id);
        if (i < 0) return { ...s, cards: [e.card, ...s.cards] };
        const had = s.cards[i]!;
        const card = had.heard && !e.card.heard ? { ...e.card, heard: had.heard } : e.card;
        return { ...s, cards: s.cards.map((c, j) => (j === i ? card : c)) };
      }
      if (e.kind === 'share') {
        // A share that starts hides the panel again, even after "Show anyway" on an earlier one.
        return { ...s, sharing: e.on, showAnyway: e.on && s.sharing ? s.showAnyway : false };
      }
      if (!s.call || s.call.botId !== e.botId) return { ...s, reread: s.reread + 1 };
      if (e.kind === 'bot') return { ...s, call: { ...s.call, state: e.state, subCode: e.subCode }, botAt: a.at };
      if (e.state === 'listening') return { ...s, call: { ...s.call, listening: true, limit: null } };
      if (e.state === 'ended') return { ...s, call: { ...s.call, state: 'ended', listening: false } };
      return { ...s, call: { ...s.call, listening: false, limit: e.limit ?? 'call' } };
    }
  }
}

/** Which of the panel's faces is drawn. */
export type PanelView = 'loading' | 'failed' | 'off' | 'silenced' | 'shared' | 'panel';

export function viewOf(s: PanelState): PanelView {
  if (s.phase === 'loading') return 'loading';
  if (s.phase === 'failed') return 'failed';
  if (!s.enabled) return 'off';
  if (s.silenced) return 'silenced';
  if (s.sharing && !s.showAnyway) return 'shared';
  return 'panel';
}

const ON_THE_CALL: ReadonlySet<BotState> = new Set(['in_call', 'recording']);
const BEFORE_THE_CALL: ReadonlySet<BotState> = new Set(['scheduled', 'joining', 'waiting']);

/** Something is capturing the call: the bot in it, or this computer. */
export function capturing(call: CopilotCall): boolean {
  return call.recordingLocally || ON_THE_CALL.has(call.state);
}

/** Cobrain is hearing the call now. */
export function hearing(call: CopilotCall): boolean {
  return call.listening && call.limit === null && call.state !== 'ended' && capturing(call);
}

export const LIMIT_WORDS = {
  call: 'Listening stopped: this call reached its limit',
  day: "Listening stopped: today's limit was reached",
} as const;

export interface Status { tone: 'ok' | 'off' | 'rec' | 'stopped'; text: string; note: string | null }

/** The header's second line: what Cobrain is doing, and a quiet note after it (A, H, I). */
export function statusOf(call: CopilotCall | null): Status {
  const off = (text: string): Status => ({ tone: 'off', text, note: null });
  if (!call) return off('Not listening');
  // Red is live or silent: a stop is the one thing on this line a glance must catch, so it is not
  // dressed as "Listening in English" with different words. What has stopped and what has not are said
  // in one place: the recording carries on at the ceiling, and the head says so beside the stop.
  if (call.limit) return { tone: 'stopped', text: LIMIT_WORDS[call.limit], note: capturing(call) ? 'The recording carries on' : null };
  if (call.state === 'ended' && !call.recordingLocally) return off('The call has ended');
  if (call.recordingLocally) return { tone: 'rec', text: 'Recording from this computer', note: hearing(call) ? 'Listening in English' : null };
  if (hearing(call)) return { tone: 'ok', text: 'Listening in English', note: null };
  if (BEFORE_THE_CALL.has(call.state)) return off('Not listening yet');
  return off('Not listening');
}

/**
 * The panel waits for this computer to start recording: the bot is waiting or refused and nothing
 * records yet. No push says the computer has started (the desktop app starts it), so while this
 * holds the panel reads the call again every few seconds, as the desktop app reads it (L18).
 */
export function awaitsLocalRecording(call: CopilotCall | null): boolean {
  return !!call && !call.recordingLocally && (call.state === 'waiting' || call.state === 'refused');
}
export const LOCAL_RECORDING_READ_MS = 5000;

/**
 * `tone` is the whole point of the notice being a box rather than a line.
 *
 * Three states shared one appearance -- `--surface` with a hairline -- and one of them means the call
 * is being lost: nothing is capturing it, nobody will get a summary, and the difference from the state
 * where the Mac has it covered was carried by the quietest text on the plate. `'lost'` says so in the
 * grammar the head above already uses for a recording that stopped (`cp-tone-stopped`, `--warn`).
 */
export interface Notice { icon: 'clock' | 'userx' | 'rec'; title: string; detail: string | null; hint: string | null; tone: 'lost' | 'plain' }

export const STOP_HINT = 'Stop it from the Cobrain menu bar icon';

/**
 * What Cobrain is doing about capturing the call, when it is not simply in it (artboard I, L18).
 *
 * THE PANEL NEVER NAMES THE HOST AND NEVER ASKS THE READER TO DO ANYTHING ABOUT CAPTURE (L28,
 * Mike, 2026-09-21, overturning the wording L18 shipped). Getting a call recorded is Cobrain's
 * side of the work: it joins, or this computer records instead, and which of those happened is
 * a fact about Cobrain, not an errand. "The host removed Cobrain from this call" and "Ask the
 * host to admit Cobrain" both put our mechanism in front of him and the remedy in his hands.
 *
 * What does NOT change is the one notice that costs him something. Where nothing is capturing
 * the call the panel still says so, in `lost`, because a silent loss is worse than an unwelcome
 * sentence -- it is now stated as Cobrain's failure rather than somebody else's act.
 *
 * Why the refusal's reason is gone from the screen: `bot_kicked_from_call` and
 * `bot_kicked_from_waiting_room` are diagnostic, they live on `platform.recall_bot.failure_reason`,
 * and that is where they are of use. Nothing a reader would do differs between them.
 *
 * The panel never starts or stops a recording and offers neither: the desktop app does both.
 */
export function noticeOf(call: CopilotCall | null): Notice | null {
  if (!call) return null;
  if (call.recordingLocally) {
    const refused = call.state === 'refused' || call.state === 'waiting';
    // Where nothing turned Cobrain away the head already says this computer is recording, and a
    // notice under it would say it twice. Where it did, the switch is worth a line -- as a note
    // about what Cobrain did, not an alarm, because the call is being captured either way.
    return refused
      ? { icon: 'rec', title: 'Cobrain switched to this computer', detail: 'It could not get into the call itself. The call is being recorded.', hint: STOP_HINT, tone: 'plain' }
      : null;
  }
  // Nothing is capturing this call. It is the one state here that costs him something, and it used to
  // look exactly like the one above, where the Mac has it.
  if (call.state === 'refused') {
    return { icon: 'userx', title: 'Nothing is recording this call', detail: 'Cobrain could not get into the call, and this computer is not recording either.', hint: null, tone: 'lost' };
  }
  // No promise about what happens next: the fallback needs a desktop app this panel cannot see, so a
  // sentence about it would be a guess. If Cobrain does not get in, the `lost` notice above says so.
  if (call.state === 'waiting') return { icon: 'clock', title: 'Cobrain is joining this call', detail: null, hint: null, tone: 'plain' };
  return null;
}

/** The line at the head of the cards: why there are none, or what has stopped (B, H, I). */
export function leadOf(call: CopilotCall | null, cards: number): string | null {
  if (!call) return 'No call is on. Cards appear here during a call with a client.';
  if (call.limit) {
    return cards > 0 ? 'The cards already shown stay below.' : 'No more cards on this call.';
  }
  if (call.state === 'ended' && !call.recordingLocally) return cards > 0 ? 'The call has ended. The cards shown during it stay here.' : 'The call has ended.';
  if (hearing(call)) return cards > 0 ? null : 'Listening. A card appears when the call touches the playbook or the account.';
  if (call.state === 'refused') return cards > 0 ? 'Listening stopped when Cobrain left. Cards shown before stay.' : 'Nothing to listen to: Cobrain is not in the call.';
  if (BEFORE_THE_CALL.has(call.state) && !call.recordingLocally) return 'Nothing to listen to yet: Cobrain is not in the call, and this computer is not recording.';
  return cards > 0 ? null : 'Not listening.';
}

const at = (c: CopilotCard) => { const t = Date.parse(c.shownAt); return Number.isNaN(t) ? 0 : t; };

/**
 * Newest first, with open notes kept under the answer they belong to (artboard A draws them so). A notes
 * card names its objection (`objection.entryId`), and goes under the newest answer to that objection; one
 * that names none goes under the answer or fact shown just before it, and stands alone when there is none.
 */
export function orderCards(cards: CopilotCard[]): CopilotCard[] {
  const oldestFirst = [...cards].sort((a, b) => at(a) - at(b) || a.id.localeCompare(b.id));
  const groups: CopilotCard[][] = [];
  for (const c of oldestFirst) {
    const last = groups[groups.length - 1];
    const head = last?.[0];
    const owner = c.kind === 'notes' && c.objection
      ? [...groups].reverse().find((g) => g[0]!.kind === 'answer' && g[0]!.objection?.entryId === c.objection!.entryId)
      : undefined;
    if (owner) owner.push(c);
    else if (c.kind === 'notes' && !c.objection && last && head && (head.kind === 'answer' || head.kind === 'fact')) last.push(c);
    else groups.push([c]);
  }
  return groups.reverse().flat();
}

/** "11:14", the card's time on this computer's clock. */
export function clockOf(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  return new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/** "18 Sep", spelt by the portal's one formatter and never by `Intl`, whose month is whatever this browser's ICU says ("Sept" today): the
    ask lane writes a typed answer's sources by the same table, so a card and the answer under it spell one day one way. */
export function dayOf(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  return dayMonth(new Date(t));
}

const DAY_MS = 86_400_000;

/** The ask lane has refused for the day: the person's limit or the store's. The route's only 429s are those two (`routes/ask.ts`). */
export function reachedDayLimit(e: unknown): boolean {
  return e instanceof ApiError && e.status === 429;
}

/** What the panel says when a typed question gets no answer (B). The ask bar's lane words its refusals for the bar's own page: a day's
    allowance in dollars, a provider's failure in the provider's own words. This window sits beside a client call and carries no money and
    no engineer's voice, so every sentence here is the panel's own and none of the lane's words is drawn. Both of the lane's days start at
    midnight UTC; that moment is said on this computer's clock, as a card's time is, never as "UTC", and with "tomorrow" whenever it is not
    on the person's own today: "from 02:00" alone, read at four in the afternoon, says the hour has already gone. A 400 from this box is a
    question over the lane's length (an empty one never leaves the box), and the words stay in the box to be cut. */
export function askFailureWords(e: unknown, now: Date = new Date()): string {
  if (reachedDayLimit(e)) {
    const again = new Date(Math.floor(now.getTime() / DAY_MS) * DAY_MS + DAY_MS);
    const today = again.getFullYear() === now.getFullYear() && again.getMonth() === now.getMonth() && again.getDate() === now.getDate();
    return `Today's limit for typed questions was reached. You can ask again from ${clockOf(again.toISOString())}${today ? '' : ' tomorrow'}.`;
  }
  if (e instanceof ApiError && e.status === 400) return 'That question is too long. Shorten it and ask again.';
  return 'Cobrain could not answer that just now.';
}

/** The approved answer's footer, in parts: the playbook and version, who approved it, when (L3). */
export function approvalParts(reply: NonNullable<CopilotCard['reply']>): string[] {
  return [`${reply.playbook} v${reply.version}`, reply.approvedBy ? `approved by ${reply.approvedBy}` : null, dayOf(reply.approvedAt) || null]
    .filter((p): p is string => !!p);
}

/** An open note's source, in parts: its label, and its date when the push carries one (L14). */
export function sourceParts(source: { label: string; at: string | null }): string[] {
  return [source.label, dayOf(source.at) || null].filter((p): p is string => !!p);
}

/** What the window's title says, so the desktop app can shrink the window to the bar (its `page-title-updated`). */
export function titleOf(view: PanelView): string {
  return view === 'silenced' || view === 'shared' ? 'Cobrain co-pilot (hidden)' : 'Cobrain co-pilot';
}
